import React, { useState } from 'react';
import { 
  Box, 
  Container, 
  AppBar, 
  Toolbar, 
  Typography, 
  Button,
  Link,
  Menu,
  MenuItem,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Collapse,
  Tooltip
} from '@mui/material';
import ExpandMore from '@mui/icons-material/ExpandMore';
import MenuIcon from '@mui/icons-material/Menu';
import Facebook from '@mui/icons-material/Facebook';
import Instagram from '@mui/icons-material/Instagram';
import LinkedIn from '@mui/icons-material/LinkedIn';
import YouTube from '@mui/icons-material/YouTube';
import Close from '@mui/icons-material/Close';
import ExpandLess from '@mui/icons-material/ExpandLess';
import Home from '@mui/icons-material/Home';
import { useLocation, Link as RouterLink } from 'react-router';
import { havenColors } from '../../theme/theme';
import logo from '../../assets/images/HAVEN-logo.png';
import havenProLogo from '../../assets/images/HavenRGB-PRO w_grad horz.svg';
import { useCookiePreferences } from '../cookie-consent/CookieConsentManager';

interface LayoutProps {
  children: React.ReactNode;
}

interface NavigationMenuItem {
  title: string;
  path: string;
  new?: boolean;
}

const resourcePages: NavigationMenuItem[] = [
  { title: 'Product Help & FAQs', path: '/support' },
  { title: 'Guides & Articles', path: '/resources' },
  { title: 'Contact Support', path: '/support/contact' }
];

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const [solutionsMenu, setSolutionsMenu] = useState<null | HTMLElement>(null);
  const [productsMenu, setProductsMenu] = useState<null | HTMLElement>(null);
  const [resourcesMenu, setResourcesMenu] = useState<null | HTMLElement>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSolutionsOpen, setMobileSolutionsOpen] = useState(false);
  const { openPreferences, PreferenceCenter } = useCookiePreferences();
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false);
  const [mobileResourcesOpen, setMobileResourcesOpen] = useState(false);
  const resourcesActive = ['/resources', '/support', '/blog'].some(
    path => location.pathname === path || location.pathname.startsWith(`${path}/`)
  );

  const handleResourcesClose = () => {
    setResourcesMenu(null);
  };

  const handleSolutionsClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setSolutionsMenu(event.currentTarget);
  };

  const handleSolutionsClose = () => {
    setSolutionsMenu(null);
  };

  const handleProductsClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setProductsMenu(event.currentTarget);
  };

  const handleProductsClose = () => {
    setProductsMenu(null);
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
    setMobileSolutionsOpen(false);
    setMobileProductsOpen(false);
    setMobileResourcesOpen(false);
  };

  const handleMobileSolutionsToggle = () => {
    setMobileSolutionsOpen(!mobileSolutionsOpen);
  };

  const handleMobileProductsToggle = () => {
    setMobileProductsOpen(!mobileProductsOpen);
  };

  const solutionPages: NavigationMenuItem[] = [
    { title: 'Demand Control Ventilation', path: '/residential-demand-control-ventilation' },
    { title: 'Active Air Management', path: '/haven-products' },
    { title: 'Radon Mitigation', path: '/smart-home-radon-mitigation' },
    { title: 'Wildfire Checklist', path: '/wildfire-infiltration-checklist' }
  ];

  const productPages: NavigationMenuItem[] = [
    { title: 'Room Monitor', path: '/room-monitor' },
    { title: 'Central Monitor', path: '/central-air-monitor' },
    { title: 'Central Controller', path: '/central-controller' }
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar 
        position="static" 
                  sx={{ 
            background: 'white',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            borderBottom: `1px solid #E5E5E5`
          }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', py: 1, px: { xs: 2, lg: 2, xl: 3 }, minWidth: 0 }}>
          <Link
            component={RouterLink}
            to="/"
            sx={{
              display: 'flex',
              alignItems: 'center',
              textDecoration: 'none',
              color: '#9BB3BF'
            }}
          >
            <Box width={200} height={55}
              component="img"
              src={logo}
              alt="HAVEN"
              sx={{
                height: { xs: 46, lg: 40, xl: 55 },
                width: 'auto'
              }} />
          </Link>
          
          <Box
            sx={{
              display: { xs: 'none', lg: 'flex' },
              alignItems: 'center',
              gap: { lg: 0.25, xl: 2 },
              minWidth: 0,
              '&& > .MuiButton-root': {
                minWidth: 'auto',
                px: { lg: 0.75, xl: 1 },
                fontSize: { lg: '0.78rem', xl: '1rem' },
              },
            }}
          >
            <Button
              onClick={handleProductsClick}
              endIcon={<ExpandMore />}
              sx={{
                fontWeight: 600,
                textTransform: 'uppercase',
                fontSize: '1rem',
                color: '#9BB3BF',
                fontFamily: 'Rubik, sans-serif',
                '&:hover': { backgroundColor: `${havenColors.primaryTeal}10` }
              }}
            >
              Products
            </Button>
            <Menu
              anchorEl={productsMenu}
              open={Boolean(productsMenu)}
              onClose={handleProductsClose}
              sx={{ mt: 1 }}
            >
              {productPages.map((page) => (
                <MenuItem
                  key={page.path}
                  component={RouterLink}
                  to={page.path}
                  onClick={handleProductsClose}
                  sx={{
                    color: '#9BB3BF',
                    '&:hover': {
                      backgroundColor: `${havenColors.primaryTeal}10`
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    {page.title}
                    {page.new && (
                      <Box
                        sx={{
                          backgroundColor: '#00c4c1',
                          color: 'white',
                          fontSize: '0.7rem',
                          fontWeight: 'bold',
                          px: 1,
                          py: 0.25,
                          borderRadius: '4px',
                          ml: 1,
                          textTransform: 'uppercase'
                        }}
                      >
                        NEW
                      </Box>
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Menu>
            
            <Button
              onClick={handleSolutionsClick}
              endIcon={<ExpandMore />}
              sx={{
                fontWeight: 600,
                textTransform: 'uppercase',
                fontSize: '1rem',
                color: '#9BB3BF',
                fontFamily: 'Rubik, sans-serif',
                '&:hover': { backgroundColor: `${havenColors.primaryTeal}10` }
              }}
            >
              Solutions
            </Button>
            <Menu
              anchorEl={solutionsMenu}
              open={Boolean(solutionsMenu)}
              onClose={handleSolutionsClose}
              sx={{ mt: 1 }}
            >
              {solutionPages.map((page) => (
                <MenuItem
                  key={page.path}
                  component={RouterLink}
                  to={page.path}
                  onClick={handleSolutionsClose}
                  sx={{
                    color: '#9BB3BF',
                    '&:hover': {
                      backgroundColor: `${havenColors.primaryTeal}10`
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    {page.title}
                    {page.new && (
                      <Box
                        sx={{
                          backgroundColor: '#00c4c1',
                          color: 'white',
                          fontSize: '0.7rem',
                          fontWeight: 'bold',
                          px: 1,
                          py: 0.25,
                          borderRadius: '4px',
                          ml: 1,
                          textTransform: 'uppercase'
                        }}
                      >
                        NEW
                      </Box>
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Menu>
            <Menu
              anchorEl={productsMenu}
              open={Boolean(productsMenu)}
              onClose={handleProductsClose}
              sx={{ mt: 1 }}
            >
              {productPages.map((page) => (
                <MenuItem
                  key={page.path}
                  component={RouterLink}
                  to={page.path}
                  onClick={handleProductsClose}
                  sx={{
                    color: '#9BB3BF',
                    '&:hover': {
                      backgroundColor: `${havenColors.primaryTeal}10`
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    {page.title}
                    {page.new && (
                      <Box
                        sx={{
                          backgroundColor: '#00c4c1',
                          color: 'white',
                          fontSize: '0.7rem',
                          fontWeight: 'bold',
                          px: 1,
                          py: 0.25,
                          borderRadius: '4px',
                          ml: 1,
                          textTransform: 'uppercase'
                        }}
                      >
                        NEW
                      </Box>
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Menu>
            
            <Button 
              component={RouterLink} 
              to="/homeowner-store"
              sx={{
                fontWeight: 600,
                textTransform: 'uppercase',
                fontSize: '1rem',
                color: '#9BB3BF',
                fontFamily: 'Rubik, sans-serif',
                backgroundColor: location.pathname === '/homeowner-store' ? `${havenColors.primaryTeal}10` : 'transparent',
                '&:hover': { backgroundColor: `${havenColors.primaryTeal}10` }
              }}
            >
              Store
            </Button>
            
            <Button
              id="resources-menu-button"
              onClick={event => setResourcesMenu(event.currentTarget)}
              endIcon={<ExpandMore />}
              aria-haspopup="menu"
              aria-controls={resourcesMenu ? 'resources-menu' : undefined}
              aria-expanded={Boolean(resourcesMenu)}
              sx={{
                fontWeight: 600,
                textTransform: 'uppercase',
                fontSize: '1rem',
                color: '#9BB3BF',
                fontFamily: 'Rubik, sans-serif',
                backgroundColor: resourcesActive ? `${havenColors.primaryTeal}10` : 'transparent',
                '&:hover': { backgroundColor: `${havenColors.primaryTeal}10` }
              }}
            >
              Resources
            </Button>

            <Menu
              id="resources-menu"
              anchorEl={resourcesMenu}
              open={Boolean(resourcesMenu)}
              onClose={handleResourcesClose}
              slotProps={{ list: { 'aria-labelledby': 'resources-menu-button' } }}
              sx={{ mt: 1 }}
            >
              {resourcePages.map(page => (
                <MenuItem
                  key={page.path}
                  component={RouterLink}
                  to={page.path}
                  onClick={handleResourcesClose}
                  sx={{ '&:hover': { backgroundColor: `${havenColors.primaryTeal}10` } }}
                >
                  {page.title}
                </MenuItem>
              ))}
            </Menu>
            
            <Button 
              component={RouterLink} 
              to="/about"
              sx={{
                fontWeight: 600,
                textTransform: 'uppercase',
                fontSize: '1rem',
                color: '#9BB3BF',
                fontFamily: 'Rubik, sans-serif',
                backgroundColor: location.pathname === '/about' ? `${havenColors.primaryTeal}10` : 'transparent',
                '&:hover': { backgroundColor: `${havenColors.primaryTeal}10` }
              }}
            >
              About
            </Button>
            
            {/* Portal Buttons Container */}
            <Box sx={{ display: 'flex', gap: { lg: 0.5, xl: 1 }, alignItems: 'center' }}>
              {/* My Portal Button */}
              <Tooltip title="Web portal for homeowners" arrow placement="bottom">
                <Link
                  href="https://my.haveniaq.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="My Portal, web portal for homeowners"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    textDecoration: 'none',
                    backgroundColor: 'rgba(0, 196, 193, 0.1)',
                    border: '1px solid rgba(0, 196, 193, 0.3)',
                    borderRadius: '8px',
                    padding: { lg: '7px 8px', xl: '8px 12px' },
                    transition: 'all 0.2s ease',
                    '&:hover': { 
                      backgroundColor: 'rgba(0, 196, 193, 0.2)',
                      borderColor: 'rgba(0, 196, 193, 0.5)',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 2px 8px rgba(0, 196, 193, 0.2)'
                    }
                  }}
                >
                  <Home
                    sx={{
                      height: 23,
                      width: 23,
                      mr: { lg: 0, xl: 1.2 },
                      color: havenColors.primaryTeal
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      fontSize: '0.875rem',
                      color: havenColors.primaryTeal,
                      fontFamily: 'Rubik, sans-serif',
                      letterSpacing: '0.5px',
                      display: { lg: 'none', xl: 'block' }
                    }}
                  >
                    MY PORTAL
                  </Typography>
                </Link>
              </Tooltip>

              {/* Pro Login Button */}
              <Tooltip title="Pro portal for Contractors" arrow placement="bottom">
                <Link
                  href="https://pro.haveniaq.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Pro Login, pro portal for contractors"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    textDecoration: 'none',
                    backgroundColor: 'rgba(155, 179, 191, 0.1)',
                    border: '1px solid rgba(155, 179, 191, 0.3)',
                    borderRadius: '8px',
                    padding: { lg: '3px 7px', xl: '2px 8px' },
                    transition: 'all 0.2s ease',
                    '&:hover': { 
                      backgroundColor: 'rgba(155, 179, 191, 0.2)',
                      borderColor: 'rgba(155, 179, 191, 0.5)',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 2px 8px rgba(155, 179, 191, 0.2)'
                    }
                  }}
                >
            <Box width={120} height={36}
                    component="img"
                    src={havenProLogo}
                    alt="HAVEN Pro"
                    sx={{
                      height: { lg: 30, xl: 36 },
                      width: 'auto',
                      mr: { lg: 0, xl: 1.2 }
                    }} />
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      fontSize: '0.875rem',
                      color: '#9BB3BF',
                      fontFamily: 'Rubik, sans-serif',
                      letterSpacing: '0.5px',
                      display: { lg: 'none', xl: 'block' }
                    }}
                  >
                    LOGIN
                  </Typography>
                </Link>
              </Tooltip>
            </Box>
          </Box>

          <IconButton
            onClick={handleMobileMenuToggle}
            aria-label="Open mobile menu"
            sx={{ display: { xs: 'block', lg: 'none' }, color: '#9BB3BF' }}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Mobile Menu Drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={handleMobileMenuClose}
        slotProps={{
          paper: {
            sx: { width: '280px' }
          }
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box width={145} height={40}
              component="img"
              src={logo}
              alt="HAVEN"
              sx={{
                height: 40,
                width: 'auto'
              }} />
            <IconButton onClick={handleMobileMenuClose}>
              <Close />
            </IconButton>
          </Box>
          <Divider sx={{ mb: 2 }} />
          
          <List>
            {/* Solutions Menu */}
            <ListItemButton onClick={handleMobileSolutionsToggle}>
              <ListItemText primary="Solutions" />
              {mobileSolutionsOpen ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
            <Collapse in={mobileSolutionsOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {solutionPages.map((page) => (
                  <ListItemButton 
                    key={page.path}
                    component={RouterLink} 
                    to={page.path}
                    onClick={handleMobileMenuClose}
                    sx={{ pl: 4 }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                      <ListItemText primary={page.title} />
                      {page.new && (
                        <Box
                          sx={{
                            backgroundColor: '#00c4c1',
                            color: 'white',
                            fontSize: '0.6rem',
                            fontWeight: 'bold',
                            px: 0.75,
                            py: 0.2,
                            borderRadius: '3px',
                            ml: 1,
                            textTransform: 'uppercase'
                          }}
                        >
                          NEW
                        </Box>
                      )}
                    </Box>
                  </ListItemButton>
                ))}
              </List>
            </Collapse>
            
            {/* Products Menu */}
            <ListItemButton onClick={handleMobileProductsToggle}>
              <ListItemText primary="Products" />
              {mobileProductsOpen ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
            <Collapse in={mobileProductsOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {productPages.map((page) => (
                  <ListItemButton 
                    key={page.path}
                    component={RouterLink} 
                    to={page.path}
                    onClick={handleMobileMenuClose}
                    sx={{ pl: 4 }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                      <ListItemText primary={page.title} />
                      {page.new && (
                        <Box
                          sx={{
                            backgroundColor: '#00c4c1',
                            color: 'white',
                            fontSize: '0.6rem',
                            fontWeight: 'bold',
                            px: 0.75,
                            py: 0.2,
                            borderRadius: '3px',
                            ml: 1,
                            textTransform: 'uppercase'
                          }}
                        >
                          NEW
                        </Box>
                      )}
                    </Box>
                  </ListItemButton>
                ))}
              </List>
            </Collapse>
            
            {/* Store */}
            <ListItemButton 
              component={RouterLink} 
              to="/homeowner-store"
              onClick={handleMobileMenuClose}
            >
              <ListItemText primary="Store" />
            </ListItemButton>
            
            {/* Resources */}
            <ListItemButton
              onClick={() => setMobileResourcesOpen(!mobileResourcesOpen)}
              aria-expanded={mobileResourcesOpen}
              aria-controls={mobileResourcesOpen ? 'mobile-resources-menu' : undefined}
              selected={resourcesActive}
            >
              <ListItemText primary="Resources" />
              {mobileResourcesOpen ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
            <Collapse in={mobileResourcesOpen} timeout="auto" unmountOnExit>
              <List id="mobile-resources-menu" component="div" disablePadding>
                {resourcePages.map(page => (
                  <ListItemButton
                    key={page.path}
                    component={RouterLink}
                    to={page.path}
                    onClick={handleMobileMenuClose}
                    sx={{ pl: 4 }}
                  >
                    <ListItemText primary={page.title} />
                  </ListItemButton>
                ))}
              </List>
            </Collapse>
            
            {/* About */}
            <ListItemButton 
              component={RouterLink} 
              to="/about"
              onClick={handleMobileMenuClose}
            >
              <ListItemText primary="About" />
            </ListItemButton>
            
            <Divider sx={{ my: 2 }} />
            
            {/* My Portal */}
            <ListItemButton 
              component="a"
              href="https://my.haveniaq.com"
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleMobileMenuClose}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Home sx={{ color: havenColors.primaryTeal, mr: 1 }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  MY PORTAL
                </Typography>
              </Box>
            </ListItemButton>
            
            {/* Pro Portal */}
            <ListItemButton 
              component="a"
              href="https://pro.haveniaq.com"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Pro Login, pro portal for contractors"
              onClick={handleMobileMenuClose}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <Box width={80} height={24}
                  component="img"
                  src={havenProLogo}
                  alt="HAVEN Pro"
                  sx={{
                    height: 24,
                    mr: 1
                  }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  PRO LOGIN
                </Typography>
              </Box>
            </ListItemButton>
          </List>
        </Box>
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1 }}>
        {children}
      </Box>

      <Box
        component="footer"
        sx={{
          py: 6,
          backgroundColor: havenColors.darkGray,
          color: 'white',
          mt: 'auto'
        }}
      >
        <Container maxWidth="lg">
          {/* Footer Content */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 6, mb: 4 }}>
            {/* Logo Section */}
            <Box sx={{ flex: 1, minWidth: '200px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Box width={174} height={48}
                  component="img"
                  src={logo}
                  alt="HAVEN"
                  sx={{
                    height: 48,
                    width: 'auto',
                    filter: 'brightness(0) invert(1)'
                  }} />
              </Box>
            </Box>
            
            {/* Homeowners Column */}
            <Box sx={{ flex: 1, minWidth: '200px' }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Homeowners</Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <Link component={RouterLink} to="/homeowner-start" sx={{ color: 'inherit', textDecoration: 'none' }}>
                  HAVEN for Homeowners
                </Link>
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <Link component={RouterLink} to="/support" sx={{ color: 'inherit', textDecoration: 'none' }}>
                  Support for Homeowners
                </Link>
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Phone: 1-888-96-HAVEN
              </Typography>
              {/* Social Media Icons for Homeowners */}
              <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                <IconButton 
                  component="a"
                  href="https://www.facebook.com/haven.tzoa"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Follow HAVEN on Facebook"
                  sx={{ 
                    width: 32, 
                    height: 32, 
                    backgroundColor: havenColors.primaryTeal,
                    color: 'white',
                    '&:hover': { backgroundColor: havenColors.primaryTeal, opacity: 0.8 }
                  }}>
                  <Facebook sx={{ fontSize: 18 }} />
                </IconButton>
                <IconButton 
                  component="a"
                  href="https://www.instagram.com/haven_tzoa/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Follow HAVEN on Instagram"
                  sx={{ 
                    width: 32, 
                    height: 32, 
                    backgroundColor: havenColors.primaryTeal,
                    color: 'white',
                    '&:hover': { backgroundColor: havenColors.primaryTeal, opacity: 0.8 }
                  }}>
                  <Instagram sx={{ fontSize: 18 }} />
                </IconButton>
                <IconButton 
                  component="a"
                  href="https://www.linkedin.com/company/3521229"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Follow HAVEN on LinkedIn"
                  sx={{ 
                    width: 32, 
                    height: 32, 
                    backgroundColor: havenColors.primaryTeal,
                    color: 'white',
                    '&:hover': { backgroundColor: havenColors.primaryTeal, opacity: 0.8 }
                  }}>
                  <LinkedIn sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            </Box>
            
            {/* Professionals Column */}
            <Box sx={{ flex: 1, minWidth: '200px' }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Professionals</Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <Link component={RouterLink} to="/become-a-pro" sx={{ color: 'inherit', textDecoration: 'none' }}>
                  HAVEN for HVAC Pros
                </Link>
              </Typography>

              <Typography variant="body2" sx={{ mb: 1 }}>
                <Link component={RouterLink} to="/support/pro" sx={{ color: 'inherit', textDecoration: 'none' }}>
                Support for Pros
                </Link>
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <Link component={RouterLink} to="/support/pro/installation" sx={{ color: 'inherit', textDecoration: 'none' }}>
                Pro Installation Guides
                </Link>
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Phone: 1-833-4-IAQ-PRO
              </Typography>
              {/* Social Media Icons for Professionals */}
              <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                <IconButton 
                  component="a"
                  href="https://www.facebook.com/HAVENProIAQ/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Follow HAVEN Pro on Facebook"
                  sx={{ 
                    width: 32, 
                    height: 32, 
                    backgroundColor: havenColors.primaryTeal,
                    color: 'white',
                    '&:hover': { backgroundColor: havenColors.primaryTeal, opacity: 0.8 }
                  }}>
                  <Facebook sx={{ fontSize: 18 }} />
                </IconButton>
                <IconButton 
                  component="a"
                  href="https://www.instagram.com/haven_pro/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Follow HAVEN Pro on Instagram"
                  sx={{ 
                    width: 32, 
                    height: 32, 
                    backgroundColor: havenColors.primaryTeal,
                    color: 'white',
                    '&:hover': { backgroundColor: havenColors.primaryTeal, opacity: 0.8 }
                  }}>
                  <Instagram sx={{ fontSize: 18 }} />
                </IconButton>
                <IconButton 
                  component="a"
                  href="https://www.linkedin.com/company/haveniaq/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Follow HAVEN Pro on LinkedIn"
                  sx={{ 
                    width: 32, 
                    height: 32, 
                    backgroundColor: havenColors.primaryTeal,
                    color: 'white',
                    '&:hover': { backgroundColor: havenColors.primaryTeal, opacity: 0.8 }
                  }}>
                  <LinkedIn sx={{ fontSize: 18 }} />
                </IconButton>
                <IconButton 
                  component="a"
                  href="https://www.youtube.com/channel/UCOuahYaxJHMoDZphUiSnmuQ"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Follow HAVEN Pro on YouTube"
                  sx={{ 
                    width: 32, 
                    height: 32, 
                    backgroundColor: havenColors.primaryTeal,
                    color: 'white',
                    '&:hover': { backgroundColor: havenColors.primaryTeal, opacity: 0.8 }
                  }}>
                  <YouTube sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            </Box>
            
            {/* Company Column */}
            <Box sx={{ flex: 1, minWidth: '200px' }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Company</Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <Link component={RouterLink} to="/about" sx={{ color: 'inherit', textDecoration: 'none' }}>
                  Our Team
                </Link>
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <Link component={RouterLink} to="/return-policy" sx={{ color: 'inherit', textDecoration: 'none' }}>
                  Return Policy
                </Link>
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <Link component={RouterLink} to="/privacy" sx={{ color: 'inherit', textDecoration: 'none' }}>
                  Privacy Policy
                </Link>
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <Link component={RouterLink} to="/tos" sx={{ color: 'inherit', textDecoration: 'none' }}>
                  Terms of Service
                </Link>
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <Link component={RouterLink} to="/cookies" sx={{ color: 'inherit', textDecoration: 'none' }}>
                  Cookie Policy
                </Link>
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <Link 
                  component="button"
                  onClick={openPreferences}
                  sx={{ 
                    color: 'inherit', 
                    textDecoration: 'none',
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    font: 'inherit',
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                >
                  Cookie Preferences
                </Link>
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <Link 
                  href="https://ca.indeed.com/cmp/Tzoa?from=univSerpAboutLink&attributionid=univSerpCompanyLogo" 
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: 'inherit', textDecoration: 'none' }}
                >
                Careers
                </Link>
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, fontWeight: 600 }}>
                HAVEN HQ
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.875rem', lineHeight: 1.4 }}>
                3456 Gardner Crt<br />
                Burnaby, BC V5G 3K4<br />
                Canada
              </Typography>
            </Box>
          </Box>

          {/* Copyright */}
          <Box sx={{ 
            textAlign: 'center', 
            pt: 3, 
            borderTop: '1px solid rgba(255,255,255,0.1)',
            opacity: 0.8 
          }}>
            <Typography variant="body2">
              Copyright © 2026 HAVEN IAQ
            </Typography>
          </Box>
        </Container>
      </Box>
      <PreferenceCenter />
    </Box>
  );
};

export default Layout;
